import * as Hechima from '../../../../../vendor/hechima/hechima.js';
import { REGISTRATION_OFFICE_NAMES } from './data/registrationOfficeNames';

export interface HechimaSegment {
  key: string;
  candidates: string[];
}

type HechimaDictEntry = {
  reading: string;
  word: string;
  pos: number;
};

type HechimaConnection = {
  init: (paths: { wasmJs: string; dataUrl: string }) => Promise<unknown>;
  convert: (yomi: string) => Promise<HechimaSegment[] | null>;
  dictAdd: (reading: string, word: string, pos?: number) => Promise<unknown>;
  dictList: () => Promise<HechimaDictEntry[] | null>;
};

let connPromise: Promise<HechimaConnection> | null = null;

const dictEntryKey = (reading: string, word: string): string => `${reading}\t${word}`;

// Mozc's stock dictionary doesn't know the place names printed on Japanese
// number plates, so they're registered as user-dictionary entries once per
// worker lifetime, right after the engine finishes loading. The user
// dictionary persists across reloads (OPFS), and dictAdd doesn't dedupe on
// its own, so entries already present are skipped to avoid piling up
// duplicates every time the kiosk reloads the page.
const registerRegistrationOfficeNames = async (conn: HechimaConnection): Promise<void> => {
  const existing = await conn.dictList();
  const alreadyRegistered = new Set(
    (existing ?? []).map((entry) => dictEntryKey(entry.reading, entry.word)),
  );

  const missing = REGISTRATION_OFFICE_NAMES.filter(
    ({ surface, reading }) => !alreadyRegistered.has(dictEntryKey(reading, surface)),
  );

  await Promise.all(missing.map(({ surface, reading }) => conn.dictAdd(reading, surface)));
};

const getConn = (): Promise<HechimaConnection> => {
  if (!connPromise) {
    const worker = new Worker('/vendor/hechima/hechima-worker.js');
    const conn = Hechima.connectWorker(worker, { maxCands: 20 }) as HechimaConnection;
    connPromise = conn.init({
      wasmJs: '/vendor/hechima-wasm/hechima-wasm.js',
      dataUrl: '/vendor/hechima-wasm/mozc.data',
    })
      .then(() => registerRegistrationOfficeNames(conn))
      .then(() => conn);
  }
  return connPromise;
};

// Stitches per-segment candidates into full-string suggestions: the best guess
// (first candidate of every segment), then alternates swapping one segment at a time.
export const buildFullCandidates = (segments: HechimaSegment[], maxResults: number): string[] => {
  if (!segments.length) {
    return [];
  }

  const bestPerSegment = segments.map((segment) => segment.candidates[0] ?? segment.key);
  const results = [bestPerSegment.join('')];

  segments.forEach((segment, segmentIndex) => {
    segment.candidates.slice(1).forEach((alternate) => {
      if (results.length >= maxResults) {
        return;
      }
      const swapped = bestPerSegment.map((candidate, i) => (i === segmentIndex ? alternate : candidate)).join('');
      if (!results.includes(swapped)) {
        results.push(swapped);
      }
    });
  });

  return results;
};

// Raw per-segment candidates, for segment-by-segment conversion UI.
export const getMozcSegments = async (input: string): Promise<HechimaSegment[] | null> => {
  try {
    const conn = await getConn();
    return await conn.convert(input);
  } catch (error) {
    console.error('[Mozc WASM] segment conversion failed', error);
    return null;
  }
};

const getMozcCandidates = async (input: string, maxResults = 20): Promise<string[]> => {
  try {
    const conn = await getConn();
    const segments = await conn.convert(input);
    if (!segments) {
      return [];
    }
    return buildFullCandidates(segments, maxResults);
  } catch (error) {
    console.error('[Mozc WASM] conversion failed', error);
    return [];
  }
};

export default getMozcCandidates;
